import React, { FC } from "react";
import { Modal, Tabs, Button } from "antd";
import { AccruedTimeLog, AccruedTimeLogProps } from "../accrued-time-log";
import styles from "./matter-details-modal.css";
import {
  AccruedFee,
  Phase,
  CumulativeAccruedFee,
  CompletionLog,
  GpmThreshold,
} from "../../data-types";
import { BillingTab } from "./billing-tab";
import { ProgressTab } from "./progress-tab";
import { WriteOffProjectionTab } from "./write-off-projection-tab";
import { useHasInvoicesQuery } from "~/graphql/query";

const { ["modal"]: modalClassName, ["body-container"]: bodyContainerClassName } = styles as any;

type MatterTimeLogModalProps = {
  show: boolean;
  onClose: () => void;
  accruedFee: AccruedFee;
  cumulativeAccruedFees: CumulativeAccruedFee[];
  phases: Phase[];
  matterCompletionPercentage: number;
  onTogglePhaseComplete: (phaseId: number, completed: boolean) => void;
  onToggleTaskComplete: (taskId: number, completed: boolean) => void;
  onCompletionPercentageChange: (percentage: number) => void;
  onPhaseCompletionPercentageChange: (phaseId: number, percentage: number) => void;
  completionLogs: CompletionLog[];
  startDate?: Date;
  deadline?: Date;
  onDeadlineChange: (deadline: Date) => void;
  gpmThreshold?: GpmThreshold;
} & AccruedTimeLogProps;

export const MatterDetailsModal: FC<MatterTimeLogModalProps> = ({
  show,
  matterTitle,
  clientName,
  onClose,
  accruedFee,
  cumulativeAccruedFees,
  phases,
  loading,
  currentGpm,
  gpmSource,
  targetGpm,
  currentGpmRiskLevel,
  accruedTimes,
  matterCompletionPercentage,
  onTogglePhaseComplete,
  onCompletionPercentageChange,
  onPhaseCompletionPercentageChange,
  completionLogs,
  startDate,
  deadline,
  onDeadlineChange,
  gpmThreshold,
}) => {
  const { data: hasInvoices } = useHasInvoicesQuery();

  return (
    <Modal
      title={`${matterTitle} - ${clientName}`}
      open={show}
      width="90%"
      style={{ top: "96px" }}
      onCancel={onClose}
      className={modalClassName}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div className={bodyContainerClassName}>
        <Tabs type="card">
          <Tabs.TabPane tab="Accrued Time" key="1">
            <div style={{ marginTop: "10px" }}>
              {accruedFee.subMatters && accruedFee.subMatters.length > 0 ? (
                <Tabs
                  type="card"
                  defaultActiveKey={accruedFee.subMatters[0].matterId.toString()}
                  items={accruedFee.subMatters.map((subMatter) => ({
                    key: subMatter.matterId.toString(),
                    title: subMatter.matterTitle,
                    label: subMatter.matterTitle,
                    children: (
                      <div style={{ marginTop: "10px" }}>
                        <AccruedTimeLog
                          accruedTimes={subMatter.accruedTimes}
                          currentGpm={subMatter.actualGpm}
                          currentGpmRiskLevel={subMatter.actualGpmRiskLevel}
                          targetGpm={subMatter.gpmDangerThreshold}
                          gpmSource={subMatter.gpmSource}
                          loading={loading}
                          matterTitle={subMatter.matterTitle}
                          clientName={subMatter.clientName}
                          currencySymbol={subMatter.currencySymbol}
                        />
                      </div>
                    ),
                  }))}
                ></Tabs>
              ) : (
                <AccruedTimeLog
                  accruedTimes={accruedTimes}
                  currentGpm={currentGpm}
                  currentGpmRiskLevel={currentGpmRiskLevel}
                  targetGpm={targetGpm}
                  gpmSource={gpmSource}
                  loading={loading}
                  matterTitle={matterTitle}
                  clientName={clientName}
                  currencySymbol={accruedFee.currencySymbol}
                />
              )}
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Billing" key="2">
            <BillingTab
              invoices={accruedFee.invoices}
              accruedFee={accruedFee}
              hasInvoices={hasInvoices?.hasInvoices ?? false}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Progress" key="3">
            <ProgressTab
              completionPercentage={matterCompletionPercentage}
              phases={phases}
              onTogglePhaseComplete={onTogglePhaseComplete}
              onCompletionPercentageChange={onCompletionPercentageChange}
              onPhaseCompletionPercentageChange={onPhaseCompletionPercentageChange}
              cumulativeAccruedFees={cumulativeAccruedFees}
              accruedFee={accruedFee}
              completionLogs={completionLogs}
              currencySymbol={accruedFee.currencySymbol}
              startDate={startDate}
              deadline={deadline}
              onDeadlineChange={onDeadlineChange}
            />
          </Tabs.TabPane>
          {hasInvoices && (
            <Tabs.TabPane tab="Write-off Projection" key="4">
              <WriteOffProjectionTab
                accruedFee={accruedFee}
                currencySymbol={accruedFee.currencySymbol}
                gpmThreshold={gpmThreshold}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      </div>
    </Modal>
  );
};
